/**
 * SEO helpers for the WordPress-backed blog (/blog and /blog/[slug]) and its
 * live sitemap (/sitemap-blog.xml). Pure functions: no fetching, so they run
 * the same in getStaticProps, the sitemap API route and the offline tests
 * (scripts/test-blog-seo.ts).
 */
import { GUIDE_POSTS_MOVED_TO_DOCS } from '@/constants/guidePostsMovedToDocs.mjs';

export const SITE_URL = 'https://easy-peasy.ai';
export const BLOG_PUBLISHER_LOGO = `${SITE_URL}/images/logo.png`;

/**
 * WordPress categories served under /blog. "Guide" posts moved here when
 * /guide was retired; the ones the docs replace redirect to the docs instead.
 */
export const BLOG_CATEGORIES = ['Uncategorized', 'Guide'];

const CONTENT_IMAGE_CDN = `${SITE_URL}/cdn-cgi/image/quality=95,format=auto,width=1000/`;

export type BlogPostSummary = {
  slug: string;
  title?: string | null;
  excerpt?: string | null;
  date?: string | null;
  dateGmt?: string | null;
  modifiedGmt?: string | null;
  featuredImage?: { node?: { sourceUrl?: string | null } | null } | null;
  categories?: { nodes?: { name?: string | null }[] | null } | null;
};

export function isMovedToDocs(slug: string): boolean {
  return Object.prototype.hasOwnProperty.call(GUIDE_POSTS_MOVED_TO_DOCS, slug);
}

/** True when /blog/<slug> renders this post (and doesn't redirect to the docs). */
export function isServedBlogPost(post: BlogPostSummary): boolean {
  if (!post?.slug || isMovedToDocs(post.slug)) return false;
  const names = (post.categories?.nodes || []).map((c) => c?.name || '');
  return names.some((name) => BLOG_CATEGORIES.includes(name));
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  hellip: '…',
  mdash: '—',
  ndash: '–',
  rsquo: '’',
  lsquo: '‘',
  rdquo: '”',
  ldquo: '“',
  laquo: '«',
  raquo: '»',
  bull: '•',
  middot: '·',
  times: '×',
  trade: '™',
  reg: '®',
  copy: '©',
  deg: '°',
  euro: '€',
  pound: '£',
  rarr: '→',
  larr: '←',
};

function decodeOnce(text: string): string {
  return text.replace(
    /&(#x[0-9a-f]+|#\d+|[a-z]+);/gi,
    (match, entity: string) => {
      if (entity[0] === '#') {
        const code =
          entity[1] === 'x' || entity[1] === 'X'
            ? parseInt(entity.slice(2), 16)
            : parseInt(entity.slice(1), 10);
        if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) {
          return match;
        }
        try {
          return String.fromCodePoint(code);
        } catch {
          return match;
        }
      }
      return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
    },
  );
}

/**
 * Decodes HTML entities. WordPress text reaches us entity-encoded (excerpts
 * keep the `&mdash;` the posts are written with), and React escapes the `&`
 * again, so an undecoded excerpt shows up in Google as "&mdash;". Two passes
 * cover text that was encoded twice (`&amp;mdash;`).
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return '';
  const once = decodeOnce(text);
  return once === text ? once : decodeOnce(once);
}

export function htmlToPlainText(html: string): string {
  if (!html) return '';
  const withoutTags = html
    .replace(/<(script|style)\b[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]*>/g, ' ');
  return decodeHtmlEntities(withoutTags).replace(/\s+/g, ' ').trim();
}

/**
 * Meta description from a WordPress excerpt (or the post content when there
 * is none). WordPress's automatic excerpts are ~55 words and end in "[…]";
 * this drops that marker and cuts on a word boundary.
 */
export function toMetaDescription(html: string, maxLength = 155): string {
  let text = htmlToPlainText(html)
    .replace(/\s*\[(?:…|\.\.\.)\]\s*$/, '')
    .replace(/\s*(?:Continue reading|Read more)\b.*$/i, '')
    .trim();
  if (text.length <= maxLength) return text;
  text = text.slice(0, maxLength - 1);
  const lastSpace = text.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.6) text = text.slice(0, lastSpace);
  return `${text.replace(/[\s,;:—–-]+$/, '')}…`;
}

/** WordPress GMT timestamps ("2026-10-07T06:23:24") as ISO 8601 UTC. */
export function toIsoUtc(gmt?: string | null): string | undefined {
  if (!gmt) return undefined;
  const value = /(?:Z|[+-]\d{2}:?\d{2})$/.test(gmt) ? gmt : `${gmt}Z`;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

const ATTRIBUTE_VALUE = String.raw`\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]+)`;

function readAttribute(attributes: string, name: string): string | null {
  const match = attributes.match(
    new RegExp(
      `(?:^|\\s)${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`,
      'i',
    ),
  );
  if (!match) return null;
  return match[1] ?? match[2] ?? match[3] ?? null;
}

function removeAttributes(attributes: string, names: string[]): string {
  return names.reduce(
    (result, name) =>
      result.replace(
        new RegExp(`(?:^|\\s)${name}(?:${ATTRIBUTE_VALUE})?(?=\\s|$)`, 'gi'),
        ' ',
      ),
    attributes,
  );
}

/**
 * Serves post images through Cloudflare image resizing while keeping each
 * image's own attributes (alt text, width/height, class). The previous
 * rewrite replaced every <img> with a bare `<img src>`, so the posts lost all
 * their alt text and the layout shifted as images loaded. srcset/sizes are
 * dropped because they point at the unresized WordPress originals; every
 * image after the first loads lazily.
 */
export function rewriteContentImages(html: string): string {
  if (!html) return '';
  let index = 0;
  return html.replace(/<img\b([^>]*?)\s*\/?>/gi, (tag, attributes: string) => {
    const src = readAttribute(attributes, 'src');
    if (!src) return tag;
    const optimized =
      /^https?:\/\//i.test(src) && !src.includes('/cdn-cgi/image/')
        ? `${CONTENT_IMAGE_CDN}${src}`
        : src;
    const kept = removeAttributes(attributes, [
      'src',
      'srcset',
      'sizes',
      'loading',
      'decoding',
      'fetchpriority',
    ])
      .replace(/\s+/g, ' ')
      .trim();
    const lazy = index > 0 ? ' loading="lazy"' : '';
    index += 1;
    return `<img src="${optimized}"${kept ? ` ${kept}` : ''}${lazy} decoding="async" />`;
  });
}

export function blogPostUrl(slug: string): string {
  return `${SITE_URL}/blog/${slug}`;
}

/** Stringified JSON-LD, safe to inline in a <script> tag. */
export function serializeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

export function buildBlogPostingJsonLd(post: {
  slug: string;
  title: string;
  description: string;
  image?: string | null;
  datePublished?: string;
  dateModified?: string;
  authorName?: string | null;
}) {
  const url = blogPostUrl(post.slug);
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        '@id': `${url}#article`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        url,
        headline: post.title.slice(0, 110),
        description: post.description || undefined,
        image: post.image ? [post.image] : undefined,
        datePublished: post.datePublished,
        dateModified: post.dateModified || post.datePublished,
        author: post.authorName
          ? { '@type': 'Person', name: post.authorName }
          : { '@type': 'Organization', name: 'Easy-Peasy.AI', url: SITE_URL },
        publisher: {
          '@type': 'Organization',
          name: 'Easy-Peasy.AI',
          url: SITE_URL,
          logo: { '@type': 'ImageObject', url: BLOG_PUBLISHER_LOGO },
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: `${SITE_URL}/blog`,
          },
          { '@type': 'ListItem', position: 3, name: post.title, item: url },
        ],
      },
    ],
  };
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** The /sitemap-blog.xml document: /blog plus every post it serves. */
export function buildBlogSitemapXml(posts: BlogPostSummary[]): string {
  const served = posts.filter(isServedBlogPost);
  const lastmods = served
    .map((post) => toIsoUtc(post.modifiedGmt) || toIsoUtc(post.dateGmt))
    .filter((value): value is string => Boolean(value))
    .sort();
  const newest = lastmods[lastmods.length - 1];

  const entries = [
    `<url><loc>${SITE_URL}/blog</loc>${newest ? `<lastmod>${newest}</lastmod>` : ''}</url>`,
    ...served.map((post) => {
      const lastmod = toIsoUtc(post.modifiedGmt) || toIsoUtc(post.dateGmt);
      const image = post.featuredImage?.node?.sourceUrl;
      return [
        `<url><loc>${escapeXml(blogPostUrl(post.slug))}</loc>`,
        lastmod ? `<lastmod>${lastmod}</lastmod>` : '',
        image
          ? `<image:image><image:loc>${escapeXml(image)}</image:loc></image:image>`
          : '',
        '</url>',
      ].join('');
    }),
  ];

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">',
    ...entries,
    '</urlset>',
    '',
  ].join('\n');
}
